import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '@/lib/authService';

export interface ResolvedWorkItemStatus {
  work_item_id: string;
  item_type: string | null;
  item_subtype: string | null;
  raw_status: string;
  base_status: string;
  resolved_status: string;
  resolved_label: string;
  source: string;
  timing_status: string | null;
  is_terminal: boolean;
  is_operationally_complete: boolean;
  workflow_state: string | null;
}

interface UseWorkItemStatusResult {
  status: ResolvedWorkItemStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetch the single-source-of-truth resolved status for a work item.
 * Uses /api/v1/work-items/:id/status which considers workflow state,
 * timing, payment status, and appointment reschedule state.
 */
export function useWorkItemStatus(workItemId: string | null | undefined): UseWorkItemStatusResult {
  const [status, setStatus] = useState<ResolvedWorkItemStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!workItemId) {
      setStatus(null);
      setError(null);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/work-items/${encodeURIComponent(workItemId)}/status`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Status check failed (${response.status})`);
      }

      const data = await response.json();
      if (data.success && data.data) {
        setStatus(data.data as ResolvedWorkItemStatus);
      } else {
        throw new Error(data.error || 'Invalid status response');
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load status');
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    void fetchStatus();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchStatus]);

  return {
    status,
    loading,
    error,
    refetch: fetchStatus,
  };
}
